import type { AgentDefinitionStaleBaseWarning } from '@erp/rpc';

/**
 * Decisions for the agent editor's draft controls.
 *
 * A user has at most one personal draft. Opening Edit used to resume that draft
 * even when the screen was showing a different version, with no way to delete it
 * or copy the version on screen. These functions keep that choice in the UI.
 */

export type AgentVersionSnapshot = {
  id: string;
  version: number;
  status: 'draft' | 'finalized';
  isPinned: boolean;
  createdBy: string | null;
  parentVersionId: string | null;
};

export type DraftConflictPlan = {
  kind: 'conflict';
  draftId: string;
  draftVersion: number;
  parentVersion: number | null;
  viewedVersion: number;
  viewedIsPublished: boolean;
  copiedFromViewedVersion: boolean;
};

export type EditPlan =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'not_finalized' }
  | { kind: 'already_editing' }
  | { kind: 'create' }
  | DraftConflictPlan;

export type EditPlanKind = EditPlan['kind'];

export type EditTooltipKey =
  | 'hidden'
  | 'create'
  | 'conflict'
  | 'not_finalized'
  | 'loading'
  | 'unavailable';

export type EditButtonMode = 'hidden' | 'enabled' | 'disabled';

export type DraftNotice =
  | { kind: 'hidden' }
  | {
      kind: 'editing';
      draftVersion: number;
      parentVersion: number | null;
      parentIsPublished: boolean;
    }
  | {
      kind: 'elsewhere';
      draftId: string;
      draftVersion: number;
      parentVersion: number | null;
      viewedVersion: number;
    };

export type AgentEditorTab =
  | 'agent'
  | 'tools'
  | 'procedures'
  | 'task-management'
  | 'resources'
  | 'permissions'
  | 'versions'
  | 'sessions'
  | 'evals';

export type ImportMenuState = 'hidden' | 'blocked' | 'ready';

const EDIT_TOOLTIP_BY_KIND: Record<EditPlanKind, EditTooltipKey> = {
  already_editing: 'hidden',
  create: 'create',
  conflict: 'conflict',
  not_finalized: 'not_finalized',
  loading: 'loading',
  unavailable: 'unavailable',
};

const EXACT_AGENT_TABS: Record<string, AgentEditorTab> = {
  '/authenticated/agents/$agentDefinitionId/tools': 'tools',
  '/authenticated/agents/$agentDefinitionId/procedures': 'procedures',
  '/authenticated/agents/$agentDefinitionId/task-management': 'task-management',
  '/authenticated/agents/$agentDefinitionId/resources': 'resources',
  '/authenticated/agents/$agentDefinitionId/permissions': 'permissions',
  '/authenticated/agents/$agentDefinitionId/versions': 'versions',
};

export function editTooltipKey(kind: EditPlanKind): EditTooltipKey {
  return EDIT_TOOLTIP_BY_KIND[kind];
}

export function editButtonMode(kind: EditPlanKind): EditButtonMode {
  if (kind === 'already_editing') return 'hidden';
  if (kind === 'create' || kind === 'conflict') return 'enabled';
  return 'disabled';
}

export function snapshotVersion<T extends AgentVersionSnapshot>(
  version: T | null,
): AgentVersionSnapshot | null {
  if (!version) return null;
  return {
    id: version.id,
    version: version.version,
    status: version.status,
    isPinned: version.isPinned,
    createdBy: version.createdBy,
    parentVersionId: version.parentVersionId,
  };
}

export function resolveCurrentVersion<T>(
  selected: T | null,
  published: T | null,
): T | null {
  return selected ?? published;
}

export function isOwnEditableDraft(input: {
  archived: boolean;
  status: 'draft' | 'finalized' | null;
  createdBy: string | null | undefined;
  userId: string | null | undefined;
}): boolean {
  if (input.archived || input.status !== 'draft') return false;
  if (!input.userId) return false;
  return input.createdBy === input.userId;
}

export function isRecordPresent<T>(value: T | null | undefined): value is T {
  return value != null;
}

export function forkSource<D, T extends { id: string; version: number }>(
  definition: D | null | undefined,
  version: T | null,
  archived: boolean,
): T | null {
  if (definition == null) return null;
  if (!version) return null;
  if (archived) return null;
  return version;
}

export function canFork(input: {
  hasDefinition: boolean;
  hasVersion: boolean;
  archived: boolean;
}): boolean {
  if (!input.hasDefinition) return false;
  if (!input.hasVersion) return false;
  return !input.archived;
}

export function personalDraft(
  versions: readonly AgentVersionSnapshot[],
  userId: string | null | undefined,
): AgentVersionSnapshot | null {
  if (!userId) return null;
  return versions.find((version) => ownsDraft(version, userId)) ?? null;
}

export function parentVersionNumber(
  version: { parentVersionId: string | null },
  versions: readonly { id: string; version: number }[],
): number | null {
  if (!version.parentVersionId) return null;
  const parent = versions.find((entry) => entry.id === version.parentVersionId);
  return parent?.version ?? null;
}

export function planEditClick(input: {
  archived: boolean;
  versionsLoaded: boolean;
  viewed: AgentVersionSnapshot | null;
  versions: readonly AgentVersionSnapshot[];
  userId: string | null | undefined;
}): EditPlan {
  if (!input.versionsLoaded) return { kind: 'loading' };
  if (input.archived || !input.viewed) return { kind: 'unavailable' };
  return planLoadedEdit(
    input.viewed,
    personalDraft(input.versions, input.userId),
    input.versions,
  );
}

export function buildDraftNotice(input: {
  archived: boolean;
  versionsLoaded: boolean;
  viewed: AgentVersionSnapshot | null;
  versions: readonly AgentVersionSnapshot[];
  userId: string | null | undefined;
  publishedVersionId: string | null;
}): DraftNotice {
  if (!canShowDraftNotice(input)) return { kind: 'hidden' };
  const draft = personalDraft(input.versions, input.userId);
  if (!draft || !input.viewed) return { kind: 'hidden' };
  return noticeForDraft(
    input.viewed,
    draft,
    input.versions,
    input.publishedVersionId,
  );
}

export type StartAgentEditDeps = {
  archived: boolean;
  hasDefinition: boolean;
  viewed: AgentVersionSnapshot | null;
  userId: string | null | undefined;
  loadVersions: () => Promise<readonly AgentVersionSnapshot[] | null>;
  openConflict: (plan: DraftConflictPlan) => void;
  fork: () => Promise<void>;
  onVersionsUnavailable: () => void;
};

export async function startAgentEdit(deps: StartAgentEditDeps): Promise<void> {
  if (!canStartEdit(deps)) return;
  const versions = await deps.loadVersions();
  if (!versions) {
    deps.onVersionsUnavailable();
    return;
  }
  await applyLoadedEditPlan(
    planEditClick({
      archived: false,
      versionsLoaded: true,
      viewed: deps.viewed,
      versions,
      userId: deps.userId,
    }),
    deps,
  );
}

export function importMenuState(input: {
  hasVersion: boolean;
  isEditable: boolean;
  isDirty: boolean;
}): ImportMenuState {
  if (!input.hasVersion) return 'hidden';
  if (input.isEditable && input.isDirty) return 'blocked';
  return 'ready';
}

export function agentDefinitionActiveTab(
  routeId: string | undefined,
): AgentEditorTab {
  if (!routeId) return 'agent';
  return nestedAgentTab(routeId) ?? EXACT_AGENT_TABS[routeId] ?? 'agent';
}

export function procedureToolList(
  version: { tools: { toolType: string }[] } | null,
  enabledTools: readonly string[],
): { toolType: string }[] {
  if (version) return version.tools;
  return enabledTools.map((toolType) => ({ toolType }));
}

export type FinalizeFollowUp =
  | { status: 'finalized' }
  | { status: 'stale_base'; warning: AgentDefinitionStaleBaseWarning }
  | { status: 'aborted' };

export function reactToFinalize(
  outcome: FinalizeFollowUp,
  actions: {
    showStale: (warning: AgentDefinitionStaleBaseWarning) => void;
    closeAndToast: () => void;
  },
): void {
  if (outcome.status === 'stale_base') {
    actions.showStale(outcome.warning);
    return;
  }
  if (outcome.status === 'aborted') return;
  actions.closeAndToast();
}

export function deletedAtOf(
  definition: { deletedAt?: string | null } | null | undefined,
): string | null | undefined {
  if (!definition) return undefined;
  return definition.deletedAt;
}

export function versionsOf(
  versionList: { versions: readonly AgentVersionSnapshot[] } | undefined,
): readonly AgentVersionSnapshot[] | undefined {
  if (!versionList) return undefined;
  return versionList.versions;
}

export function pinnedVersionIdOf(
  definition: { pinnedVersionId?: string | null } | null | undefined,
): string | null | undefined {
  if (!definition) return undefined;
  return definition.pinnedVersionId;
}

export function versionHistoryReady(
  succeeded: boolean,
  failed: boolean,
): boolean {
  if (succeeded) return true;
  return failed;
}

export function userIdOf(
  user: { id: string } | null | undefined,
): string | null {
  if (!user) return null;
  return user.id;
}

export function editorViewState(input: {
  deletedAt: string | null | undefined;
  viewed: AgentVersionSnapshot | null;
  versions: readonly AgentVersionSnapshot[] | undefined;
  versionsLoaded: boolean;
  userId: string | null;
  publishedVersionId: string | null | undefined;
}): {
  isArchived: boolean;
  isEditable: boolean;
  editPlan: EditPlan;
  draftNotice: DraftNotice;
} {
  const archived = isDeleted(input.deletedAt);
  const versions = presentVersions(input.versions);
  const publishedVersionId = presentId(input.publishedVersionId);
  return {
    isArchived: archived,
    isEditable: isOwnEditableDraft({
      archived,
      status: viewedStatus(input.viewed),
      createdBy: viewedAuthor(input.viewed),
      userId: input.userId,
    }),
    editPlan: planEditClick({
      archived,
      versionsLoaded: input.versionsLoaded,
      viewed: input.viewed,
      versions,
      userId: input.userId,
    }),
    draftNotice: buildDraftNotice({
      archived,
      versionsLoaded: input.versionsLoaded,
      viewed: input.viewed,
      versions,
      userId: input.userId,
      publishedVersionId,
    }),
  };
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function isDeleted(deletedAt: string | null | undefined): boolean {
  return deletedAt != null;
}

function presentVersions(
  versions: readonly AgentVersionSnapshot[] | undefined,
): readonly AgentVersionSnapshot[] {
  return versions ?? [];
}

function presentId(id: string | null | undefined): string | null {
  return id ?? null;
}

function viewedStatus(
  viewed: AgentVersionSnapshot | null,
): 'draft' | 'finalized' | null {
  if (!viewed) return null;
  return viewed.status;
}

function viewedAuthor(viewed: AgentVersionSnapshot | null): string | null {
  if (!viewed) return null;
  return viewed.createdBy;
}

function ownsDraft(version: AgentVersionSnapshot, userId: string): boolean {
  return version.status === 'draft' && version.createdBy === userId;
}

function isViewingDraft(
  viewed: AgentVersionSnapshot,
  draft: AgentVersionSnapshot | null,
): boolean {
  if (!draft) return false;
  return viewed.id === draft.id;
}

function planLoadedEdit(
  viewed: AgentVersionSnapshot,
  draft: AgentVersionSnapshot | null,
  versions: readonly AgentVersionSnapshot[],
): EditPlan {
  if (isViewingDraft(viewed, draft)) return { kind: 'already_editing' };
  if (viewed.status !== 'finalized') return { kind: 'not_finalized' };
  if (!draft) return { kind: 'create' };
  return conflictPlan(viewed, draft, versions);
}

function conflictPlan(
  viewed: AgentVersionSnapshot,
  draft: AgentVersionSnapshot,
  versions: readonly AgentVersionSnapshot[],
): DraftConflictPlan {
  return {
    kind: 'conflict',
    draftId: draft.id,
    draftVersion: draft.version,
    parentVersion: parentVersionNumber(draft, versions),
    viewedVersion: viewed.version,
    viewedIsPublished: viewed.isPinned,
    copiedFromViewedVersion: draft.parentVersionId === viewed.id,
  };
}

function canShowDraftNotice(input: {
  archived: boolean;
  versionsLoaded: boolean;
}): boolean {
  if (input.archived) return false;
  return input.versionsLoaded;
}

function noticeForDraft(
  viewed: AgentVersionSnapshot,
  draft: AgentVersionSnapshot,
  versions: readonly AgentVersionSnapshot[],
  publishedVersionId: string | null,
): DraftNotice {
  const parentVersion = parentVersionNumber(draft, versions);
  if (viewed.id === draft.id) {
    return {
      kind: 'editing',
      draftVersion: draft.version,
      parentVersion,
      parentIsPublished: isParentPublished(draft, publishedVersionId),
    };
  }
  return {
    kind: 'elsewhere',
    draftId: draft.id,
    draftVersion: draft.version,
    parentVersion,
    viewedVersion: viewed.version,
  };
}

function isParentPublished(
  draft: AgentVersionSnapshot,
  publishedVersionId: string | null,
): boolean {
  if (!publishedVersionId) return false;
  return draft.parentVersionId === publishedVersionId;
}

function canStartEdit(deps: StartAgentEditDeps): boolean {
  if (deps.archived) return false;
  if (!deps.hasDefinition) return false;
  return deps.viewed !== null;
}

async function applyLoadedEditPlan(
  plan: EditPlan,
  deps: StartAgentEditDeps,
): Promise<void> {
  if (plan.kind === 'conflict') {
    deps.openConflict(plan);
    return;
  }
  if (plan.kind === 'create') await deps.fork();
}

function nestedAgentTab(routeId: string): AgentEditorTab | null {
  if (routeId.includes('/evals')) return 'evals';
  if (routeId.includes('/sessions')) return 'sessions';
  return null;
}
